import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGet, adminPost, adminPatch } from "../../lib/adminApi";
import { PageHeader, ErrorBanner, OvergeslagenBanner, formatDate, LoadingRow, EmptyRow } from "../../components/admin/AdminUI";

const ROLLEN = [
  { key: "admin", label: "Admin", omschrijving: "Alle toegang: bedrijven, werknemers, agenda, instellingen" },
  { key: "aanmeter", label: "Aanmeter", omschrijving: "Aanmeetdag-tablet + eigen agenda" },
  { key: "podotherapeut", label: "Podotherapeut", omschrijving: "Pijn-signalen + werknemer-dossiers" },
];

export default function GebruikersPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const q = useQuery({
    queryKey: ["gebruikers"],
    queryFn: () => adminGet("/api/admin/gebruikers"),
  });

  const items = q.data?.items || [];

  return (
    <div data-testid="gebruikers-page">
      <PageHeader
        title="Gebruikers"
        subtitle="Beheer admins, aanmeters en podotherapeuten — iemand kan meerdere rollen hebben"
        actions={
          <button type="button" onClick={() => setOpen(true)} data-testid="btn-gebruiker-nieuw"
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700">
            + Nieuwe gebruiker
          </button>
        }
      />
      <ErrorBanner error={q.error} />
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="gebruiker" meervoud="gebruikers"
        testid="gebruikers-overgeslagen"
      />
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Naam</th>
              <th className="px-4 py-2 text-left">E-mail</th>
              <th className="px-4 py-2 text-left">Rollen</th>
              <th className="px-4 py-2 text-left">MFA</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.isLoading ? <LoadingRow cols={6} /> :
              items.length === 0 ? <EmptyRow cols={6} label="Nog geen gebruikers" /> :
              items.map((u) => (
                <tr key={u.user_id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium">{u.naam}</td>
                  <td className="px-4 py-2">{u.email}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(u.rollen && u.rollen.length ? u.rollen : [u.rol]).filter(Boolean).map((r) => (
                        <span key={r} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{ROLLEN.find((x) => x.key === r)?.label || r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs">{u.mfa_enabled ? "Aan" : "Uit"}</td>
                  <td className="px-4 py-2 text-xs">{u.actief ? <span className="text-emerald-700">Actief</span> : <span className="text-rose-700">Gedeactiveerd</span>}</td>
                  <td className="px-4 py-2 text-right">
                    <button type="button" onClick={() => setEdit(u)} className="text-xs font-medium text-blue-600 hover:underline">Bewerken</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {open ? <NieuweGebruikerModal onClose={() => setOpen(false)} onSuccess={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["gebruikers"] }); }} /> : null}
      {edit ? <BewerkGebruikerModal gebruiker={edit} onClose={() => setEdit(null)} onSuccess={() => { setEdit(null); qc.invalidateQueries({ queryKey: ["gebruikers"] }); }} /> : null}
    </div>
  );
}

function genereerWachtwoord() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$";
  let out = "";
  for (let i = 0; i < 16; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function NieuweGebruikerModal({ onClose, onSuccess }) {
  const [naam, setNaam] = useState("");
  const [email, setEmail] = useState("");
  const [rollen, setRollen] = useState(["aanmeter"]);
  const [wachtwoord, setWachtwoord] = useState(genereerWachtwoord());
  const [copied, setCopied] = useState(false);

  const mut = useMutation({
    mutationFn: (body) => adminPost("/api/admin/gebruikers", body),
    onSuccess: () => { toast.success("Gebruiker aangemaakt — deel het wachtwoord op een veilige manier"); onSuccess(); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Aanmaken mislukt"),
  });

  function toggleRol(r) {
    setRollen((curr) => curr.includes(r) ? curr.filter((x) => x !== r) : [...curr, r]);
  }

  function copyWachtwoord() {
    navigator.clipboard?.writeText(wachtwoord);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const valid = naam.trim().length >= 2 && email.includes("@") && rollen.length > 0 && wachtwoord.length >= 12;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">Nieuwe gebruiker</h3>
        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Naam</span>
            <input value={naam} onChange={(e) => setNaam(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">E-mail</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <div className="block text-sm">
            <span className="font-medium text-slate-700">Rollen (kies één of meer)</span>
            <div className="mt-2 space-y-2">
              {ROLLEN.map((r) => (
                <label key={r.key} className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2">
                  <input type="checkbox" checked={rollen.includes(r.key)} onChange={() => toggleRol(r.key)} className="mt-0.5" />
                  <span>
                    <div className="text-sm font-medium text-slate-900">{r.label}</div>
                    <div className="text-xs text-slate-500">{r.omschrijving}</div>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Tijdelijk wachtwoord</span>
            <div className="mt-1 flex gap-2">
              <input value={wachtwoord} onChange={(e) => setWachtwoord(e.target.value)} className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono text-xs" />
              <button type="button" onClick={() => setWachtwoord(genereerWachtwoord())} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700">Genereer</button>
              <button type="button" onClick={copyWachtwoord} className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700">{copied ? "OK" : "Kopieer"}</button>
            </div>
            <span className="mt-1 block text-xs text-slate-500">Deel dit wachtwoord veilig met de gebruiker. Min 12 tekens.</span>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">Annuleren</button>
          <button type="button" disabled={!valid || mut.isPending}
            onClick={() => mut.mutate({ naam: naam.trim(), email: email.trim(), rollen, initial_password: wachtwoord })}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">
            {mut.isPending ? "Aanmaken…" : "Aanmaken"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BewerkGebruikerModal({ gebruiker, onClose, onSuccess }) {
  const [naam, setNaam] = useState(gebruiker.naam || "");
  const [rollen, setRollen] = useState(gebruiker.rollen?.length ? gebruiker.rollen : [gebruiker.rol]);
  const [actief, setActief] = useState(gebruiker.actief);

  const mut = useMutation({
    mutationFn: (body) => adminPatch(`/api/admin/gebruikers/${gebruiker.user_id}`, body),
    onSuccess: () => { toast.success("Gebruiker bijgewerkt"); onSuccess(); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Bijwerken mislukt"),
  });

  function toggleRol(r) {
    setRollen((curr) => curr.includes(r) ? curr.filter((x) => x !== r) : [...curr, r]);
  }

  const valid = naam.trim().length >= 2 && rollen.length > 0;

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
        <h3 className="text-base font-semibold text-slate-900">Gebruiker bewerken</h3>
        <p className="mt-0.5 text-xs text-slate-500">{gebruiker.email}</p>
        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Naam</span>
            <input value={naam} onChange={(e) => setNaam(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
          </label>
          <div className="block text-sm">
            <span className="font-medium text-slate-700">Rollen</span>
            <div className="mt-2 space-y-2">
              {ROLLEN.map((r) => (
                <label key={r.key} className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2">
                  <input type="checkbox" checked={rollen.includes(r.key)} onChange={() => toggleRol(r.key)} className="mt-0.5" />
                  <span>
                    <div className="text-sm font-medium text-slate-900">{r.label}</div>
                    <div className="text-xs text-slate-500">{r.omschrijving}</div>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={actief} onChange={(e) => setActief(e.target.checked)} />
            <span className="text-slate-700">Account actief</span>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">Annuleren</button>
          <button type="button" disabled={!valid || mut.isPending}
            onClick={() => mut.mutate({ naam: naam.trim(), rollen, actief })}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">
            {mut.isPending ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}
